import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { nanoid } from 'nanoid'
import {
  Input,
  Button,
  Modal,
  Checkbox,
  Table,
  Popconfirm,
  Divider,
  Switch,
  notification,
  message,
  Upload,
  Tooltip,
  ConfigProvider,
} from 'antd'
import {
  UploadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons'

import { EditOutlined } from '@ant-design/icons'
import { BuilderContext } from '../../../../store/store.Builder'
import { SettingOutlined } from '@ant-design/icons'
import {
  SvgCloseIcon,
  SvgMoreIcon,
} from '../../../../../../../components/svg/SvgComponents'
import UserSearchBar from '../../../../../../../components/AvatarRoles/components/UserSearchBar/UserSearchBar'
import styles from './OptionsInput.module.css'
import { set, toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons'

notification.config({
  placement: 'top',
  duration: 3, // La notification disparaîtra après 5 secondes.
  top: 50, // Cela détermine la marge depuis le haut. Ajustez selon vos besoins.
})

// Renomme une option sans jamais écraser une valeur distincte du label
// (ex. un code importé) : les réponses déjà enregistrées et les traitements
// qui s'appuient sur cette valeur resteraient orphelins.
// Si la valeur n'est qu'une copie du label, elle suit le nouveau label.
const renameOption = (option, newLabel) =>
  option.value === option.label
    ? { ...option, label: newLabel, value: newLabel }
    : { ...option, label: newLabel }

const OptionsInput = observer(({ value = [], onChange }) => {
  const [columnSelected, setColumnSelected] = useState({})

  const builderContext = useContext(BuilderContext)
  const [optionValue, setOptionValue] = useState('')
  const [editingValue, setEditingValue] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)
  const [isModalVisible, setModalVisible] = useState(false)
  const [isEditable, setIsEditable] = useState(false)
  const [editedValues, setEditedValues] = useState([...value])
  const [tempValue, setTempValue] = useState('')
  const [sortOrder, setSortOrder] = useState('asc') // État pour suivre l'ordre actuel
  const [isSwitchImport, setIsSwitchImport] = useState(false)

  useEffect(() => {
    // Mettre à jour editedValues lorsque value change (par exemple, lorsque les données sont chargées initialement).
    setEditedValues([...value])
  }, [value])

  // Fonction pour inverser l'ordre de tri
  const toggleSortOrder = () => {
    console.log('toggleSortOrder')
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortOrder(newOrder)

    // Effectuer le tri ici au lieu de le faire dans useEffect
    const sorted = [...value]
    sorted.sort((a, b) => {
      const labelA = String(a.label || '')
      const labelB = String(b.label || '')

      return newOrder === 'asc'
        ? labelA.localeCompare(labelB)
        : labelB.localeCompare(labelA)
    })
    onChange(sorted)
  }

  const [permissions, setPermissions] = useState(() => {
    console.log('SETPERMISSION VALUE', toJS(value))
    const initialPermissions = {}
    value.forEach((option) => {
      if (option.rights) {
        // Vérifiez si "rights" existe
        initialPermissions[option.id] = {}
        option.rights.forEach((right) => {
          initialPermissions[option.id][right] = true
        })
      }
    })
    return initialPermissions
  })

  console.log('permissions', toJS(permissions))

  const handleAddOption = useCallback(() => {
    if (optionValue === '') {
      notification.warning({ message: 'Le label est requis !' })
      return
    }

    const isValueExist = value.some((option) => option.value === optionValue)
    const isLabelExist = value.some((option) => option.label === optionValue)

    if (isValueExist && isLabelExist) {
      notification.warning({
        message:
          'Cette option existe déjà en tant que valeur et en tant que label dans la liste des options !',
      })

      return
    }

    if (isValueExist) {
      notification.warning({
        message:
          'Cette option existe déjà en tant que valeur dans la liste des options!',
      })
      return
    }

    if (isLabelExist) {
      notification.warning({
        message:
          'Cette option existe déjà en tant que label dans la liste des options!',
      })
      return
    }

    // Ajouter l'option avec le droit "ALLeveryoneALL" par défaut
    const newOption = {
      id: nanoid(),
      value: optionValue,
      label: optionValue,
      rights: ['ALLeveryoneALL'],
    }

    onChange([...value, newOption])
    setOptionValue('')
  }, [optionValue, value, onChange])

  useEffect(() => {
    const updatedPermissions = {}

    value.forEach((option) => {
      if (option.rights) {
        updatedPermissions[option.id] = {}
        option.rights.forEach((right) => {
          updatedPermissions[option.id][right] = true
        })
      }
    })

    setPermissions(updatedPermissions)
  }, [value])

  const moveOption = (index, direction) => {
    if (
      (index === 0 && direction === 'up') ||
      (index === value.length - 1 && direction === 'down')
    ) {
      return
    }
    const newValue = [...value]
    const temp = newValue[index]
    newValue[index] = newValue[index + (direction === 'up' ? -1 : 1)]
    newValue[index + (direction === 'up' ? -1 : 1)] = temp
    onChange(newValue)
  }

  const handleSaveEditing = useCallback(
    (newValue) => {
      // Vérifier si la nouvelle valeur ou le label existe déjà dans les autres options
      const isDuplicate = value.some(
        (option, idx) =>
          (option.value === newValue || option.label === newValue) &&
          idx !== editingIndex
      )

      if (isDuplicate) {
        notification.warning({
          message:
            'La valeur ou le label existe déjà dans la liste des options !',
        })
        return
      }

      const updatedOptions = value.map((option, index) =>
        index === editingIndex ? renameOption(option, newValue) : option
      )

      onChange(updatedOptions)
      setEditingIndex(null)
      setEditingValue('')
    },
    [editingIndex, value, onChange]
  )

  // Fonction pour sauvegarder le label
  const handleSaveLabel = (index, currentText) => {
    if (editingValue.trim() === '') {
      notification.warning({ message: 'Le label est requis !' })
      setEditingValue(currentText) // Rétablir l'ancien texte
      return
    }

    // Vérifiez si le label ou la valeur existe déjà, selon isEditable
    const isDuplicate = value.some((option, idx) => {
      const isSameIndex = idx !== index
      const isLabelDuplicate = option.label === editingValue && isSameIndex
      const isValueDuplicate =
        isEditable === false && option.value === editingValue && isSameIndex
      return isLabelDuplicate || isValueDuplicate
    })

    if (isDuplicate) {
      notification.warning({
        message: `Le ${
          isEditable === false ? 'label ou la valeur' : 'label'
        } existe déjà dans la liste des options!`,
      })
      setEditingValue(currentText) // Rétablir l'ancien texte
      return
    }

    // Mise à jour des options
    const updatedOptions = [...value]
    updatedOptions[index] =
      isEditable === false
        ? renameOption(updatedOptions[index], editingValue)
        : { ...updatedOptions[index], label: editingValue }

    onChange(updatedOptions) // Appliquez les mises à jour
    setEditingIndex(null)
    setEditingValue('') // Réinitialiser la valeur d'édition
  }

  // Fonction pour sauvegarder la valeur
  const handleSaveValue = (index, currentValue) => {
    const editedValue = editedValues[index].value

    // Vérifiez s'il y a des doublons dans editedValues ou si la valeur est vide.
    const isDuplicate = checkForDuplicates(index, editedValue)
    const isEmpty = editedValue === ''

    if (isEmpty) {
      handleEmptyValue()
      const editedValuesCopy = [...value]
      editedValuesCopy[index].value = tempValue
      setEditedValues(editedValuesCopy)
    } else if (isDuplicate) {
      handleDuplicateValue()
      const editedValuesCopy = [...value]
      editedValuesCopy[index].value = tempValue
      setEditedValues(editedValuesCopy)
    } else {
      handleValidValue(index, editedValue)
    }
  }

  const checkForDuplicates = (index, editedValue) => {
    return editedValues
      .slice(0, index)
      .concat(editedValues.slice(index + 1))
      .some((option) => option.value === editedValue)
  }

  const handleEmptyValue = () => {
    notification.warning({
      message: 'La valeur est vide!',
    })
  }

  const handleDuplicateValue = () => {
    notification.warning({
      message: 'La valeur existe déjà dans la liste des options!',
    })
  }

  const handleValidValue = (index, editedValue) => {
    // Mettez à jour la valeur d'origine avec la nouvelle valeur éditée.
    const updatedValue = [...value]
    updatedValue[index].value = editedValue

    // Appliquez les changements via la fonction onChange.
    onChange(updatedValue)
  }

  const handleRemoveOption = useCallback(
    (indexToRemove) => {
      if (value.length === 1) {
        notification.warning({
          message: 'Vous devez avoir au moins une option !',
        })
        return
      }

      onChange(value.filter((_, index) => index !== indexToRemove))
    },
    [value, onChange]
  )

  const handleEditOption = useCallback(
    (index) => {
      // Copiez la valeur actuelle en édition
      const editingOption = { ...value[index] }

      // Assurez-vous que l'option en édition n'a pas de doublons ou de valeur vide
      const isDuplicateOrEmpty = value.some(
        (option, idx) =>
          idx !== index &&
          (option.value === editingOption.value || !editingOption.value)
      )

      if (isDuplicateOrEmpty) {
        notification.warning({
          message:
            "Impossible d'éditer cette option en raison de doublons ou de valeurs vides.",
        })
        return
      }

      setEditingIndex(index)
      setEditingValue(editingOption.value)
      setEditingValue(editingOption.label)
    },
    [value]
  )

  const handleEditLabel = useCallback(
    (index) => {
      setEditingIndex(index)
      setEditingValue(value[index].label)
    },
    [value]
  )

  const openPermissionsModal = useCallback(() => {
    setModalVisible(true)
  }, [])

  const closeModal = useCallback(() => {
    setModalVisible(false)
  }, [])

  const handleCheckboxChange = (optionId, userId, checked) => {
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [optionId]: {
        ...prevPermissions[optionId],
        [userId]: checked,
      },
    }))

    // Nouveau code pour vérifier si toutes les cases de la colonne sont cochées
    if (!checked) {
      // Si une case est décochée, décochez également la case du titre de la colonne
      setColumnSelected((prevSelected) => ({
        ...prevSelected,
        [userId]: false,
      }))
    } else {
      // Si une case est cochée, vérifiez si toutes les autres cases de la colonne sont également cochées
      const allChecked = value.every(
        (record) => permissions[record.id]?.[userId]
      )
      setColumnSelected((prevSelected) => ({
        ...prevSelected,
        [userId]: allChecked,
      }))
    }

    const updatedValue = value.map((option) => {
      if (option.id === optionId) {
        const currentRights = option.rights || []
        if (checked && !currentRights.includes(userId)) {
          return { ...option, rights: [...currentRights, userId] }
        } else if (!checked && currentRights.includes(userId)) {
          return {
            ...option,
            rights: currentRights.filter((id) => id !== userId),
          }
        }
      }
      return option
    })
    onChange(updatedValue)
  }

  const onSelect = (item) => {
    console.log(item)
    console.log('value', toJS(value))
    console.log('onChange', toJS(onChange))
    console.log('selectedField', toJS(builderContext.selectedField))

    let updatedOptionsUsers

    if (Array.isArray(builderContext.selectedField.options_users)) {
      // Si c'est déjà un tableau, ajoutez le nouvel utilisateur à la fin
      updatedOptionsUsers = [
        ...builderContext.selectedField.options_users,
        { id: nanoid(), username: item.mail },
      ]
    } else {
      // Sinon, créez un nouveau tableau avec le nouvel utilisateur comme seul élément
      updatedOptionsUsers = [{ id: nanoid(), username: item.mail }]
    }

    const updatedField = {
      ...builderContext.selectedField,
      options_users: updatedOptionsUsers,
      typeSelected: 'field',
    }

    builderContext.handleFieldUpdate(updatedField, false)
  }

  const removeUserFromOptions = (usernameToRemove) => {
    if (
      typeof usernameToRemove !== 'string' ||
      usernameToRemove.trim() === ''
    ) {
      console.error(
        'Le nom d’utilisateur à supprimer n’est pas une chaîne valide.'
      )
      return
    }

    let updatedValue = []
    let updatedOptionsUsers = []

    try {
      // Mise à jour des droits d'accès
      if (!Array.isArray(value)) {
        throw new Error('La valeur fournie n’est pas un tableau.')
      }

      updatedValue = value.map((row) => {
        if (row.rights && row.rights.includes(usernameToRemove)) {
          const updatedRights = row.rights.filter(
            (username) => username !== usernameToRemove
          )
          return { ...row, rights: updatedRights }
        }
        return row
      })

      // Mise à jour des utilisateurs dans builderContext.selectedField.options_users
      updatedOptionsUsers = builderContext.selectedField.options_users.filter(
        (userObj) => userObj.username !== usernameToRemove
      )

      onChange(updatedValue)
    } catch (error) {
      console.error('Erreur lors de la mise à jour des droits:', error)
    }

    const updatedField = {
      ...builderContext.selectedField,
      options_users: updatedOptionsUsers,
      value: updatedValue,
      typeSelected: 'field',
    }

    builderContext.handleFieldUpdate(updatedField, false)
  }

  const toggleSelectAll = (columnKey, checked) => {
    const newPermissions = { ...permissions }
    value.forEach((record) => {
      if (!newPermissions[record.id]) {
        newPermissions[record.id] = {}
      }
      newPermissions[record.id][columnKey] = checked
    })
    setColumnSelected({ ...columnSelected, [columnKey]: checked })
    setPermissions(newPermissions)

    const updatedValue = value.map((option) => {
      const currentRights = option.rights || []
      if (checked && !currentRights.includes(columnKey)) {
        return { ...option, rights: [...currentRights, columnKey] }
      } else if (!checked && currentRights.includes(columnKey)) {
        return {
          ...option,
          rights: currentRights.filter((id) => id !== columnKey),
        }
      }
      return option
    })
    onChange(updatedValue)
  }

  const inputNewValue = (
    <div className={styles.flex_line}>
      <input
        placeholder="Options"
        className={styles.input__add}
        value={optionValue}
        onChange={(e) => setOptionValue(e.target.value)}
        onPressEnter={handleAddOption}
        onKeyDown={(e) => {
          if (e.keyCode === 13) {
            handleAddOption()
          }
        }}
        required
      />
      <Button className={styles.btn__add} onClick={handleAddOption}>
        <SvgMoreIcon />
      </Button>
    </div>
  )

  const columns = [
    isEditable
      ? {
          // Render "Valeurs" field with onChange and onBlur
          dataIndex: 'value',
          key: 'value',
          fixed: 'left',
          render: (text, record, index) => {
            if (isEditable) {
              return (
                <Input
                  className={styles.input_edit_value}
                  value={text}
                  onClick={(e) => setTempValue(e.target.value)}
                  onChange={(e) => handleValueChange(e.target.value, index)}
                  onBlur={() => handleSaveValue(index, text)}
                />
              )
            }
            return <span> {isEditable ? text : null}</span>
          },
          title: isEditable ? 'Valeurs' : null,
          width: 300,
        }
      : {},
    {
      title: 'Options',
      width: 300,
      fixed: 'left',
      dataIndex: 'label',
      key: 'label',
      className: styles.column__options,
      render: (text, record, index) => {
        if (editingIndex === index) {
          return (
            <Input
              className={styles.input_edit_value}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleSaveLabel(index, text)} // Utilisation de la nouvelle fonction avec le texte actuel
            />
          )
        }
        return (
          <Input
            className={styles.input_edit_value}
            value={text}
            onClick={() => handleEditLabel(index)}
            style={{ cursor: 'pointer' }}
          />
        )
      },
    },
    {
      title: (
        <Checkbox
          checked={columnSelected['ALLeveryoneALL']}
          onChange={(e) => toggleSelectAll('ALLeveryoneALL', e.target.checked)}
        >
          Tout le monde
        </Checkbox>
      ),
      key: 'ALLeveryoneALL',
      render: (_, record) => (
        <Checkbox
          checked={permissions[record.id]?.ALLeveryoneALL}
          onChange={(e) =>
            handleCheckboxChange(record.id, 'ALLeveryoneALL', e.target.checked)
          }
        />
      ),
    },
    ...(builderContext.selectedField.options_users?.map((userObj) => ({
      title: (
        <span style={{ fontWeight: 'bold', color: '#4A90E2' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '5px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {userObj.username}{' '}
            <Popconfirm
              title="Voulez-vous vraiment supprimer l'utilisateur ?"
              onConfirm={() => removeUserFromOptions(userObj.username)}
              onCancel={() => {}}
              okText="Oui"
              cancelText="Non"
            >
              <Button className={styles.btn_edit_option}>
                <SvgCloseIcon />
              </Button>
            </Popconfirm>
          </div>
        </span>
      ),
      key: userObj.username,
      children: [
        // {
        //   title: (
        //     <Checkbox
        //       checked={columnSelected[`${userObj.username}_canInput`]}
        //       onChange={(e) =>
        //         toggleSelectAll(
        //           `${userObj.username}_canInput`,
        //           e.target.checked
        //         )
        //       }
        //     >
        //       Saisie
        //     </Checkbox>
        //   ),
        //   dataIndex: `${userObj.username}_canInput`,
        //   key: `${userObj.username}_canInput`,
        //   render: (_, record) => (
        //     <Checkbox
        //       checked={permissions[record.id]?.[`${userObj.username}_canInput`]}
        //       onChange={(e) =>
        //         handleCheckboxChange(
        //           record.id,
        //           `${userObj.username}_canInput`,
        //           e.target.checked
        //         )
        //       }
        //     />
        //   ),
        // },
        {
          title: (
            <Tooltip title="Si coché, l'utilisateur pourra voir les réponses qui contiennent cette valeur, mais il ne pourra ni saisir ni modifier.">
              <Checkbox
                checked={columnSelected[`${userObj.username}_canView`]}
                onChange={(e) =>
                  toggleSelectAll(
                    `${userObj.username}_canView`,
                    e.target.checked
                  )
                }
              >
                Lecture
              </Checkbox>
            </Tooltip>
          ),

          dataIndex: `${userObj.username}_canView`,
          key: `${userObj.username}_canView`,
          render: (_, record) => (
            <Checkbox
              checked={permissions[record.id]?.[`${userObj.username}_canView`]}
              onChange={(e) =>
                handleCheckboxChange(
                  record.id,
                  `${userObj.username}_canView`,
                  e.target.checked
                )
              }
            />
          ),
        },
        {
          title: (
            <Tooltip title="Si coché, l'utilisateur pourra voir, modifier les réponses et saisir des données avec cette valeur.">
              <Checkbox
                checked={columnSelected[`${userObj.username}_canEdit`]}
                onChange={(e) =>
                  toggleSelectAll(
                    `${userObj.username}_canEdit`,
                    e.target.checked
                  )
                }
              >
                Ecriture
              </Checkbox>
            </Tooltip>
          ),

          dataIndex: `${userObj.username}_canEdit`,
          key: `${userObj.username}_canEdit`,
          render: (_, record) => (
            <Checkbox
              checked={permissions[record.id]?.[`${userObj.username}_canEdit`]}
              onChange={(e) =>
                handleCheckboxChange(
                  record.id,
                  `${userObj.username}_canEdit`,
                  e.target.checked
                )
              }
            />
          ),
        },
      ],
    })) || []),

    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record, index) => (
        <div className={styles.action_bar}>
          <Popconfirm
            title="Voulez-vous vraiment supprimer cette option?"
            onConfirm={() => handleRemoveOption(index)}
            onCancel={() => {}}
            okText="Oui"
            cancelText="Non"
          >
            <Button className={styles.btn_edit_option}>
              <SvgCloseIcon />
            </Button>
          </Popconfirm>
          <Button
            disabled={index === 0}
            onClick={() => moveOption(index, 'up')}
          >
            <ArrowUpOutlined />
          </Button>
          <Button
            disabled={index === value.length - 1}
            onClick={() => moveOption(index, 'down')}
          >
            <ArrowDownOutlined />
          </Button>
        </div>
      ),
    },
  ]

  const html = (
    <div className={styles.container}>
      {inputNewValue}
      <div className={styles.container_options}>
        {Array.isArray(value) &&
          value.map((option, index) => (
            <div key={option.id} className={styles.line_container}>
              <Input
                value={editingIndex === index ? editingValue : option.label}
                onChange={(e) => {
                  if (editingIndex === index) {
                    setEditingValue(e.target.value)
                  }
                }}
                onClick={() => handleEditOption(index)}
                className={styles.text_options}
                style={{
                  border: editingIndex === index ? '1px solid' : 'none',
                  boxShadow: editingIndex === index ? '0 0 0px #ccc' : 'none',
                  cursor: 'pointer',
                }}
                onBlur={() => {
                  if (editingValue === '') {
                    notification.warning({
                      message: 'Le label est requis !',
                    })
                    return
                  }
                  handleSaveEditing(editingValue)
                  setEditingIndex(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // Si l'utilisateur appuie sur Enter, enregistrez la modification
                    if (editingValue === '') {
                      notification.warning({
                        message: 'Le label est requis !',
                      })
                    } else {
                      handleSaveEditing(editingValue)
                    }
                    setEditingIndex(null)
                    // Retirez le focus du champ de texte
                    e.target.blur()
                  }
                }}
              />
              {value.length > 1 && (
                <Button
                  className={styles.btn_dell_options}
                  onClick={() => handleRemoveOption(index)}
                >
                  <SvgCloseIcon />
                </Button>
              )}
            </div>
          ))}
      </div>
    </div>
  )

  const handleFileChange = (file) => {
    if (file) {
      const reader = new FileReader()

      reader.onload = function (event) {
        const processData = (data) => {
          const headers = data[0].map((header) => header.toLowerCase())
          const valueIndex = headers.indexOf('valeurs')
          const labelIndex = headers.indexOf('options')

          if (labelIndex === -1) {
            // Notification d'erreur
            notification.error({
              message: 'Erreur',
              description: 'La colonne "options" doit être présente !',
            })
            return
          }

          // Filtre et map basés sur les indices
          let optionsSet = new Set()
          let valuesSet = new Set()

          const processedOptions = data
            .slice(1)
            .filter((row) => {
              if (
                row[labelIndex] &&
                !optionsSet.has(row[labelIndex]) &&
                (!row[valueIndex] ||
                  (row[valueIndex] && !valuesSet.has(String(row[valueIndex]))))
              ) {
                optionsSet.add(row[labelIndex])

                // Assurez-vous que la valeur a une valeur valide, sinon utilisez le libellé
                const value =
                  row[valueIndex] !== undefined
                    ? String(row[valueIndex])
                    : row[labelIndex]

                valuesSet.add(value)

                return true
              }
              return false
            })
            .map((row) => ({
              id: nanoid(),
              value:
                valueIndex !== -1 && row[valueIndex] !== undefined
                  ? String(row[valueIndex])
                  : row[labelIndex], // Conversion en chaîne pour éviter les problèmes de type
              label: row[labelIndex],
              rights: ['ALLeveryoneALL'],
            }))

          // Vérifier les doublons avec les options existantes
          const uniqueOptions = processedOptions.filter((newOption) =>
            value.every(
              (existingOption) =>
                existingOption.value !== newOption.value &&
                existingOption.label !== newOption.label
            )
          )

          onChange([...value, ...uniqueOptions])

          // Notification de succès (déplacée ici)
          notification.success({
            message: 'Succès',
            description: 'Le fichier a été importé avec succès !',
          })
        }

        if (file.name.endsWith('.csv')) {
          // Traitement CSV
          const text = event.target.result
          Papa.parse(text, {
            complete: (result) => {
              processData(result.data)
            },
            header: false,
          })
        } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
          // Traitement Excel
          const workbook = XLSX.read(event.target.result, { type: 'binary' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          processData(data)
        } else {
          // Notification pour les fichiers non pris en charge
          notification.error({
            message: 'Erreur',
            description: 'Format de fichier non pris en charge !',
          })
        }
      }

      if (file.name.endsWith('.csv')) {
        reader.readAsText(file, 'ISO-8859-1')
      } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        reader.readAsBinaryString(file)
      }
    }
  }

  const handleValueChange = (newValue, index) => {
    // Créez une variable temporaire pour stocker les modifications.
    const newEditedValues = [...editedValues]
    newEditedValues[index].value = newValue

    // Mettre à jour editedValues /avec la variable temporaire.
    setEditedValues(newEditedValues)

    /** 
  // Vérifiez s'il y a des doublons dans la variable temporaire.
  const isDuplicate = newEditedValues.some(
    (option, idx) => option.value === newValue && idx !== index
  );

  if (!isDuplicate) {
    // S'il n'y a pas de doublons, mettez à jour editedValues avec la variable temporaire.
    setEditedValues(newEditedValues);
  }

  **/
  }

  console.log('value8', value)
  return (
    <div>
      <Modal
        centered
        open={isModalVisible}
        onCancel={closeModal}
        maskClosable={true}
        footer={null}
        width={1400}
        title="Options d'Affichage Personnalisées"
      >
        <div className={styles.container_modal}>
          <div className={styles.container_searchbar}>
            <div className={styles.title_head}>
              <h3>Importer des options</h3>
            </div>
            <div>
              <Switch
                checked={isSwitchImport}
                onChange={(checked) => setIsSwitchImport(checked)}
              />
            </div>
          </div>

          {isSwitchImport && (
            <div className={styles.container_import_bloc}>
              <div className={styles.container_searchbar}>
                <div className={styles.title_head}>
                  <h3>
                    Vous pouvez importer des options et des valeurs avec un
                    fichier Excel ou CSV
                  </h3>
                  <div className={styles.description_import}>
                    <ul>
                      <li>
                        La colonne "options" est obligatoire pour que
                        l'importation fonctionne.
                      </li>
                      <li>L'importation des "valeurs" est facultative.</li>
                      <li>
                        Si "valeurs" n'est pas fourni, "option" sera utilisé
                        pour remplir automatiquement "valeurs".
                      </li>
                      <li>
                        Attention, si votre fichier Excel ou CSV contient des
                        options ou des valeurs déjà présentes dans la liste,
                        elles seront ignorées et non importées.
                      </li>
                    </ul>
                  </div>
                </div>
                <Upload
                  showUploadList={false} // pour ne pas afficher la liste des fichiers uploadés
                  beforeUpload={(file) => {
                    handleFileChange(file)
                    return false
                  }}
                  accept=".csv,.xls,.xlsx"
                >
                  <Button icon={<UploadOutlined />}>Choisir un fichier</Button>
                </Upload>
              </div>
            </div>
          )}

          <Divider />
          <div className={styles.container_searchbar}>
            <div className={styles.title_head}>
              <h3>Ajouter des utilisateurs</h3>
            </div>
            <div className={styles.container_searchbar_user}>
              <UserSearchBar
                onSelect={onSelect}
                showRights={false}
                dataAPI={builderContext.selectedField.options_users}
              />
            </div>
          </div>
          <Divider />

          <div className={styles.container_searchbar}>
            <div className={styles.title_head}>
              <h3>Ajouter une option</h3>
            </div>
            {inputNewValue}
          </div>

          <Divider />

          <div className={styles.container_searchbar}>
            <div className={styles.title_head}>
              <h3>Afficher et modifier les Valeurs</h3>
            </div>
            <div>
              <Switch checked={isEditable} onChange={setIsEditable} />
            </div>
          </div>

          <Divider />
          <Button onClick={toggleSortOrder}>
            {sortOrder === 'asc' ? 'Trier ascendant' : 'Trier descendant'}{' '}
            {sortOrder === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />}
          </Button>
          <Divider />
          <div className={styles.table__modal}>
            <div className={styles.container_searchbar}>
              <div className={styles.title_head}>
                <h3>Tableau des permissions</h3>
              </div>

              <ConfigProvider
                theme={{
                  token: {
                    fontFamily: 'var(--sans-serif)',
                    colorBorderSecondary: 'rgba(224, 224, 224)',
                    colorFillAlter: 'rgba(0, 0, 0, 0.05)',
                  },
                  components: {
                    Table: {},
                  },
                }}
              >
                <Table
                  dataSource={value}
                  bordered
                  className={styles.table_permissions}
                  columns={columns.filter(
                    (column) => Object.keys(column).length > 0
                  )}
                  rowKey="id"
                  pagination={false}
                />
              </ConfigProvider>
            </div>
          </div>
        </div>
      </Modal>

      <div className={styles.btn_personnalize} onClick={openPermissionsModal}>
        <SettingOutlined /> Personnalisé
      </div>

      {html}
    </div>
  )
})

export default OptionsInput
